import React from "react";
import { FormattedMessage } from "react-intl";
import { Box, Flex, Text, Radio, Spacer } from "@chakra-ui/react";
import { isNilOrEmpty } from "utils";
import { Money } from "components/Money";
import { isWallet } from "../common";
import { MethodClassIcon } from "components/Icons";
import MoreOptionsMenu from "./MoreOptionsMenu";

const UnrecognizedPaymentMethod = ({ data }) => {
  console.error("Unrecognized payment method provided:", data);
  return null;
};

// TODO: Feels like this should probably be handled by the API and not be double checked by us,
//       unless I'm missing something (@magnus)
const supportedPaymentMethodClasses = [
  "paypal",
  "american-express",
  "discover",
  "diners-club",
  "jcb",
  "maestro",
  "electron",
  "master-card",
  "visa",
  "china-unionpay",
];

const BankName = ({ data }) => {
  let displayText = "";
  let width = 42;
  let isEllipsis = false;

  // we only want to display bank name for banking methods
  if (data.provider === "banking" && data.provider_data) {
    displayText = data.provider_data.bank_name;
    const descLength = data.display_string.length; // max should be 36 chars
    width -= descLength;

    // max width for bank name + desc can be 42 chars
    // if the total chars is more than 42 we cut the bank name and add ellipsis
    if (displayText.length > width) {
      displayText = displayText.substring(0, width - 3) + "...";
      isEllipsis = true;
    }
  }

  return (
    <Box title={isEllipsis ? data.provider_data.bank_name : null}>
      <Text>{displayText}</Text>
    </Box>
  );
};

const PaymentMethodDescription = ({ data }) =>
  isWallet(data) ? (
    <Box textAlign="right">
      <Text lineHeight={1}>
        <FormattedMessage
          defaultMessage="Wallet balance"
          description="Balance, as in money amount"
        />
      </Text>
      <Text fontSize="xs" color="secondaryText">
        <Money currency={data.processing_currency} amount={data.wallet_balance} />
      </Text>
    </Box>
  ) : (
    <Text>{data.display_string}</Text>
  );

const PaymentMethodItem = ({
  data,
  isDisabled: _isDisabled = false,
  showMenu = true,
  onComplete,
}) => {
  const isUnrecognized =
    isNilOrEmpty(data) ||
    (!isWallet(data) &&
      data.provider !== "banking" &&
      !supportedPaymentMethodClasses.includes(data.method_class));
  if (isUnrecognized) return <UnrecognizedPaymentMethod data={data} />;

  const isDisabled = (isWallet(data) && data?.provider_data?.balance <= 0) || _isDisabled;
  let ariaLabel = "A payment method";
  if (data.method_class === "paypal") {
    ariaLabel = `Paypal account with email address ${data.display_string}`;
  } else if (typeof data.display_string === "string" && data.display_string.length > 0) {
    ariaLabel = data.display_string;
  }

  return (
    <Box
      data-testid={`pm-section-${data.id}`}
      borderBottomWidth="1px"
      _last={{ borderBottomWidth: 0 }}
    >
      <Flex
        py={4}
        align="center"
        cursor={isDisabled ? "not-allowed" : "pointer"}
        as="label"
        aria-label={ariaLabel}
      >
        <Radio value={data.id} mr={4} isDisabled={isDisabled} />
        <Box opacity={isDisabled ? 0.5 : 1}>
          <MethodClassIcon name={isWallet(data) ? "wallet" : data.method_class} />
        </Box>
        <Box ml="2" opacity={isDisabled ? 0.5 : 1}>
          <BankName data={data} />
        </Box>
        <Spacer />
        <Box opacity={isDisabled ? 0.5 : 1}>
          <PaymentMethodDescription data={data} />
        </Box>
        {showMenu && !isWallet(data) ? (
          <Box ml="2">
            <MoreOptionsMenu paymentMethod={data} onComplete={onComplete} />
          </Box>
        ) : (
          <Box m="4" ml="7" />
        )}
      </Flex>
    </Box>
  );
};

export default PaymentMethodItem;
